import HttpsRoundedIcon from '@mui/icons-material/HttpsRounded';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Card, Container, Typography } from '@mui/material';
import { passwordStrength } from 'check-password-strength';
import { useFormik } from 'formik';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { object, ObjectSchema, ref, string } from 'yup';
import AuthApi from '../../apis/AuthApi';
import FormikTextField from '../../components/FormikTextField/FormikTextField';
import SnackbarContext from '../../contexts/SnackbarContext';
import { ChangePasswordDto } from '../../generated-types/changePasswordDto';
import useApi from '../../lib/useApi';
import useSharedAuthLib from '../../lib/useSharedAuthLib';

/**
 * A dto that's modified from what the backend expects. This is used explicitly with formik.
 */
export interface ChangePasswordWithConfirmDto extends ChangePasswordDto {
  confirmPassword: string;
}

/**
 * A view for a user to change their password
 */
const ChangePasswordView = () => {
  const authApi = useApi(AuthApi);
  const snackbarContext = useContext(SnackbarContext);
  const authLib = useSharedAuthLib();
  const navigate = useNavigate();
  const schema: ObjectSchema<ChangePasswordWithConfirmDto> = object({
    oldPassword: string().defined('Required'),
    newPassword: string()
      .defined('Required')
      .test(
        'password-strength',
        'Password must contain a lowercase letter, uppercase letter, number, special symbol, and be at least 8 characters long.',
        (password) => passwordStrength(password).id >= 2
      ),
    confirmPassword: string()
      .defined('Required')
      .oneOf([ref('newPassword')], 'Passwords do not match'),
  });

  const formik = useFormik<ChangePasswordWithConfirmDto>({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: schema,
    onSubmit: (val) => {
      authApi
        .ChangePassword(val)
        .then((response) => {
          switch (response.code) {
            case 200:
              snackbarContext.setAlert(
                <Alert variant="filled" severity="success">
                  Successfully changed your password!
                </Alert>
              );

              // Set the authentication - the response token will always be populated if we get a 200
              if (response.responseBody)
                authLib.SetToken(response.responseBody);

              formik.resetForm();
              break;
            case 401:
              if (response.responseBody == 'incorrect-password') {
                formik.setFieldError('oldPassword', 'Incorrect password.');
                formik.setSubmitting(false);
              } else {
                snackbarContext.setAlert(
                  <Alert variant="filled" severity="warning">
                    Login session expired. Please log back in.
                  </Alert>
                );
                snackbarContext.triggerSnackbar();
                navigate('/login');
              }

              break;
            default:
              throw Error();
          }
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              An unrecognized error occurred while changing your password.
              Please try again.
            </Alert>
          );
          formik.setSubmitting(false);
        })
        .finally(() => {
          snackbarContext.triggerSnackbar();
        });
    },
  });

  return (
    <Box height="100%" width="100%">
      <Container maxWidth="sm" sx={{ paddingY: '1em' }}>
        <Card elevation={5} sx={{ padding: '2em' }}>
          <Typography variant="h1" fontSize="3em" gutterBottom>
            Change password
          </Typography>
          <Typography marginBottom="1em">
            To change your password, complete the form below.
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <FormikTextField
              id="current-password"
              formik={formik}
              fullWidth
              label="Current Password"
              name="oldPassword"
              type="password"
              autoFocus
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              id="new-password"
              formik={formik}
              fullWidth
              label="New Password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              sx={{
                marginBottom: '1em',
              }}
            />
            <FormikTextField
              id="confirm-new-password"
              formik={formik}
              fullWidth
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              sx={{
                marginBottom: '1em',
              }}
            />
            <LoadingButton
              id="change-password-button"
              loading={formik.isSubmitting}
              loadingPosition="end"
              type="submit"
              variant="contained"
              endIcon={<HttpsRoundedIcon />}
            >
              Change Password
            </LoadingButton>
          </form>
        </Card>
      </Container>
    </Box>
  );
};

export default ChangePasswordView;
